import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  DollarSign, Calendar, Save, Plus, Trash2, Loader2,
  TrendingUp, TrendingDown, ChevronLeft, ChevronRight,
  Settings, Zap, Calculator, Check, X, CalendarRange
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { adminApi } from '../lib/api';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, isBefore } from 'date-fns';
import { es, enUS } from 'date-fns/locale';

const WEEKDAYS_ES = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const WEEKDAYS_EN = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const AdvancedPricing = () => {
  const { i18n } = useTranslation();
  const locale = i18n.language === 'es' ? es : enUS;
  const weekdays = i18n.language === 'es' ? WEEKDAYS_ES : WEEKDAYS_EN;
  
  const [pricing, setPricing] = useState(null);
  const [calendarData, setCalendarData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  // Selection mode
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [bulkPrice, setBulkPrice] = useState('');
  
  // Range selection
  const [rangeMode, setRangeMode] = useState(false);
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [rangePrice, setRangePrice] = useState('');

  const fetchPricing = async () => {
    try {
      const response = await adminApi.getPricing();
      setPricing(response.data);
    } catch (error) {
      toast.error('Error al cargar tarifas');
    } finally {
      setLoading(false);
    }
  };

  const fetchCalendar = useCallback(async () => {
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;
      const response = await adminApi.getPricingCalendar(year, month);
      setCalendarData(response.data.calendar);
    } catch (error) {
      console.error('Error fetching calendar:', error);
    }
  }, [currentMonth]);

  useEffect(() => {
    fetchPricing();
  }, []);

  useEffect(() => {
    if (pricing) {
      fetchCalendar();
    }
  }, [currentMonth, pricing, fetchCalendar]);

  const handleModeChange = async (isAdvanced) => {
    const newMode = isAdvanced ? 'ADVANCED' : 'SIMPLE';
    const updatedPricing = { ...pricing, pricing_mode: newMode };
    setPricing(updatedPricing);
    
    setSaving(true);
    try {
      await adminApi.updatePricing(updatedPricing);
      toast.success(isAdvanced ? 'Modo avanzado activado' : 'Modo simple activado');
    } catch (error) {
      toast.error('Error al cambiar modo');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSimpleConfig = async () => {
    setSaving(true);
    try {
      await adminApi.updatePricing(pricing);
      toast.success('Configuración guardada');
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDateClick = (dateStr) => {
    if (!selectionMode) return;
    
    setSelectedDates(prev => {
      if (prev.includes(dateStr)) {
        return prev.filter(d => d !== dateStr);
      }
      return [...prev, dateStr];
    });
  };

  const handleApplyBulkPrice = async () => {
    if (selectedDates.length === 0 || !bulkPrice) {
      toast.error('Selecciona fechas y precio');
      return;
    }
    
    const priceCents = Math.round(parseFloat(bulkPrice) * 100);
    
    setSaving(true);
    try {
      await adminApi.addBulkDates(selectedDates, priceCents, 'Bulk update');
      toast.success(`Precio aplicado a ${selectedDates.length} fechas`);
      setSelectedDates([]);
      setBulkPrice('');
      setSelectionMode(false);
      fetchPricing();
      fetchCalendar();
    } catch (error) {
      toast.error('Error al aplicar precios');
    } finally {
      setSaving(false);
    }
  };

  const handleApplyRangePrice = async () => {
    if (!rangeStart || !rangeEnd || !rangePrice) {
      toast.error('Completa todos los campos');
      return;
    }
    
    const priceCents = Math.round(parseFloat(rangePrice) * 100);
    
    setSaving(true);
    try {
      await adminApi.addDateRange(rangeStart, rangeEnd, priceCents, 'Range update');
      toast.success('Rango de precios aplicado');
      setRangeStart('');
      setRangeEnd('');
      setRangePrice('');
      setRangeMode(false);
      fetchPricing();
      fetchCalendar();
    } catch (error) {
      toast.error('Error al aplicar rango');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveDate = async (dateStr) => {
    try {
      await adminApi.removeCustomDate(dateStr);
      toast.success('Precio eliminado');
      fetchPricing();
      fetchCalendar();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleClearAllCustom = async () => {
    if (!window.confirm('¿Eliminar TODOS los precios personalizados?')) return;
    
    try {
      await adminApi.clearCustomDates();
      toast.success('Precios personalizados eliminados');
      fetchPricing();
      fetchCalendar();
    } catch (error) {
      toast.error('Error al limpiar');
    }
  };

  // Generate calendar grid
  const calendarGrid = useMemo(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const days = eachDayOfInterval({ start, end });
    
    // Add padding for first week
    const firstDayOfWeek = start.getDay();
    const paddingDays = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    const paddedDays = Array(paddingDays).fill(null).concat(days);
    
    return paddedDays;
  }, [currentMonth]);

  const getPriceForDate = (dateStr) => {
    const dayData = calendarData.find(d => d.date === dateStr);
    return dayData ? dayData : null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  const isAdvancedMode = pricing?.pricing_mode === 'ADVANCED';

  return (
    <div className="space-y-6" data-testid="advanced-pricing">
      {/* Mode Selector */}
      <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-xl p-6 border border-primary/20">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Settings className="w-5 h-5 text-primary" />
              {i18n.language === 'es' ? 'Modo de Tarificación' : 'Pricing Mode'}
            </h3>
            <p className="text-zinc-400 text-sm mt-1">
              {isAdvancedMode 
                ? (i18n.language === 'es' ? 'Precio personalizado por día' : 'Custom price per day')
                : (i18n.language === 'es' ? 'Precio base + precio por día' : 'Base price + daily price')
              }
            </p>
          </div>
          <div className="flex items-center gap-4">
            <span className={`text-sm ${!isAdvancedMode ? 'text-primary font-semibold' : 'text-zinc-400'}`}>
              Simple
            </span>
            <Switch
              checked={isAdvancedMode}
              onCheckedChange={handleModeChange}
              data-testid="pricing-mode-switch"
            />
            <span className={`text-sm ${isAdvancedMode ? 'text-primary font-semibold' : 'text-zinc-400'}`}>
              Avanzado
            </span>
          </div>
        </div>
      </div>

      {/* SIMPLE MODE CONFIGURATION */}
      {!isAdvancedMode && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Formula Display */}
          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <div className="flex items-center gap-3 mb-4">
              <Calculator className="w-6 h-6 text-primary" />
              <h3 className="text-lg font-semibold text-white">
                {i18n.language === 'es' ? 'Fórmula de Cálculo' : 'Calculation Formula'}
              </h3>
            </div>
            <div className="bg-black/40 rounded-lg p-4 font-mono text-center">
              <span className="text-primary">Total</span>
              <span className="text-zinc-400"> = </span>
              <span className="text-green-400">Precio Base</span>
              <span className="text-zinc-400"> + (</span>
              <span className="text-blue-400">Días</span>
              <span className="text-zinc-400"> × </span>
              <span className="text-yellow-400">Precio/Día</span>
              <span className="text-zinc-400">)</span>
            </div>
          </div>

          {/* Simple Mode Settings */}
          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-primary" />
              {i18n.language === 'es' ? 'Configuración de Precios' : 'Price Settings'}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-zinc-300 flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-green-400"></span>
                  {i18n.language === 'es' ? 'Precio Base (€)' : 'Base Price (€)'}
                </Label>
                <p className="text-xs text-zinc-500">
                  {i18n.language === 'es' 
                    ? 'Cargo fijo inicial por reserva (ej: gastos de gestión)'
                    : 'Fixed initial charge per booking (e.g., service fee)'}
                </p>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={(pricing?.base_price_cents || 0) / 100}
                  onChange={(e) => setPricing(prev => ({
                    ...prev,
                    base_price_cents: Math.round(parseFloat(e.target.value || 0) * 100)
                  }))}
                  className="bg-black/40 border-white/20 text-xl font-bold"
                  data-testid="base-price-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-zinc-300 flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-yellow-400"></span>
                  {i18n.language === 'es' ? 'Precio por Día (€)' : 'Daily Price (€)'}
                </Label>
                <p className="text-xs text-zinc-500">
                  {i18n.language === 'es' 
                    ? 'Precio que se cobra por cada día de estancia'
                    : 'Price charged for each day of stay'}
                </p>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={(pricing?.daily_price_cents || pricing?.daily_rate_cents || 0) / 100}
                  onChange={(e) => setPricing(prev => ({
                    ...prev,
                    daily_price_cents: Math.round(parseFloat(e.target.value || 0) * 100),
                    daily_rate_cents: Math.round(parseFloat(e.target.value || 0) * 100)
                  }))}
                  className="bg-black/40 border-white/20 text-xl font-bold"
                  data-testid="daily-price-input"
                />
              </div>
            </div>

            {/* Example calculation */}
            <div className="mt-6 p-4 bg-primary/10 rounded-lg border border-primary/20">
              <p className="text-sm text-zinc-300 mb-2 font-medium">
                {i18n.language === 'es' ? 'Ejemplo: Reserva de 5 días' : 'Example: 5-day booking'}
              </p>
              <p className="font-mono text-primary">
                {((pricing?.base_price_cents || 0) / 100).toFixed(2)}€ + (5 × {((pricing?.daily_price_cents || pricing?.daily_rate_cents || 0) / 100).toFixed(2)}€) = {' '}
                <span className="text-white font-bold">
                  {(((pricing?.base_price_cents || 0) + 5 * (pricing?.daily_price_cents || pricing?.daily_rate_cents || 0)) / 100).toFixed(2)}€
                </span>
              </p>
            </div>
          </div>

          {/* General Settings */}
          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <h3 className="text-lg font-semibold text-white mb-4">
              {i18n.language === 'es' ? 'Configuración General' : 'General Settings'}
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-sm text-zinc-400 mb-2 block">Mín. días</Label>
                <Input
                  type="number"
                  min="1"
                  value={pricing?.min_days || 1}
                  onChange={(e) => setPricing(prev => ({
                    ...prev,
                    min_days: parseInt(e.target.value) || 1
                  }))}
                  className="bg-black/40 border-white/20"
                />
              </div>
              <div>
                <Label className="text-sm text-zinc-400 mb-2 block">Máx. días</Label>
                <Input
                  type="number"
                  min="1"
                  value={pricing?.max_days || 30}
                  onChange={(e) => setPricing(prev => ({
                    ...prev,
                    max_days: parseInt(e.target.value) || 30
                  }))}
                  className="bg-black/40 border-white/20"
                />
              </div>
              <div>
                <Label className="text-sm text-zinc-400 mb-2 block">Antelación (h)</Label>
                <Input
                  type="number"
                  min="0"
                  value={pricing?.min_advance_hours || 2}
                  onChange={(e) => setPricing(prev => ({
                    ...prev,
                    min_advance_hours: parseInt(e.target.value) || 2
                  }))}
                  className="bg-black/40 border-white/20"
                />
              </div>
              <div>
                <Label className="text-sm text-zinc-400 mb-2 block">Descuento larga estancia (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={pricing?.long_stay_discount_percent || 0}
                  onChange={(e) => setPricing(prev => ({
                    ...prev,
                    long_stay_discount_percent: parseInt(e.target.value) || 0
                  }))}
                  className="bg-black/40 border-white/20"
                />
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button 
              onClick={handleSaveSimpleConfig}
              disabled={saving}
              className="btn-primary"
              data-testid="save-simple-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {i18n.language === 'es' ? 'Guardar Configuración' : 'Save Configuration'}
            </Button>
          </div>
        </motion.div>
      )}

      {/* ADVANCED MODE CONFIGURATION */}
      {isAdvancedMode && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Fallback Price */}
          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-primary" />
              {i18n.language === 'es' ? 'Precio por Defecto (Fallback)' : 'Default Price (Fallback)'}
            </h3>
            <p className="text-zinc-400 text-sm mb-4">
              {i18n.language === 'es' 
                ? 'Este precio se usa para días que no tienen precio personalizado asignado'
                : 'This price is used for days without a custom price'}
            </p>
            <div className="flex items-center gap-4">
              <Input
                type="number"
                step="0.01"
                min="0"
                value={(pricing?.daily_price_cents || pricing?.daily_rate_cents || 0) / 100}
                onChange={(e) => setPricing(prev => ({
                  ...prev,
                  daily_price_cents: Math.round(parseFloat(e.target.value || 0) * 100),
                  daily_rate_cents: Math.round(parseFloat(e.target.value || 0) * 100)
                }))}
                className="bg-black/40 border-white/20 w-32 text-xl font-bold"
              />
              <span className="text-zinc-400">€/día</span>
              <Button onClick={handleSaveSimpleConfig} disabled={saving} size="sm" className="btn-secondary">
                <Save className="w-4 h-4 mr-1" />
                Guardar
              </Button>
            </div>
          </div>

          {/* Calendar Controls */}
          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary" />
                {i18n.language === 'es' ? 'Calendario de Precios' : 'Pricing Calendar'}
              </h3>
              
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant={selectionMode ? 'default' : 'outline'}
                  onClick={() => {
                    setSelectionMode(!selectionMode);
                    if (selectionMode) setSelectedDates([]);
                  }}
                  className={selectionMode ? 'bg-primary text-black' : ''}
                  data-testid="selection-mode-btn"
                >
                  {selectionMode ? <Check className="w-4 h-4 mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                  {selectionMode ? 'Seleccionando...' : 'Seleccionar Fechas'}
                </Button>
                
                <Button
                  size="sm"
                  variant={rangeMode ? 'default' : 'outline'}
                  onClick={() => setRangeMode(!rangeMode)}
                  className={rangeMode ? 'bg-blue-500 text-white' : ''}
                  data-testid="range-mode-btn"
                >
                  <CalendarRange className="w-4 h-4 mr-1" />
                  Rango
                </Button>
                
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={handleClearAllCustom}
                  data-testid="clear-all-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Limpiar Todo
                </Button>
              </div>
            </div>

            {/* Range Input */}
            <AnimatePresence>
              {rangeMode && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mb-6 p-4 bg-blue-500/10 rounded-lg border border-blue-500/20"
                >
                  <p className="text-sm text-blue-400 mb-3">Aplicar precio a un rango de fechas</p>
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                    <Input
                      type="date"
                      value={rangeStart}
                      onChange={(e) => setRangeStart(e.target.value)}
                      className="bg-black/40 border-white/20"
                      placeholder="Fecha inicio"
                    />
                    <Input
                      type="date"
                      value={rangeEnd}
                      onChange={(e) => setRangeEnd(e.target.value)}
                      className="bg-black/40 border-white/20"
                      placeholder="Fecha fin"
                    />
                    <Input
                      type="number"
                      step="0.01"
                      value={rangePrice}
                      onChange={(e) => setRangePrice(e.target.value)}
                      className="bg-black/40 border-white/20"
                      placeholder="Precio (€)"
                    />
                    <Button onClick={handleApplyRangePrice} disabled={saving} className="btn-primary">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aplicar'}
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Bulk Selection Bar */}
            <AnimatePresence>
              {selectionMode && selectedDates.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="mb-6 p-4 bg-primary/10 rounded-lg border border-primary/20 flex items-center gap-4"
                >
                  <span className="text-primary font-medium">{selectedDates.length} fechas seleccionadas</span>
                  <Input
                    type="number"
                    step="0.01"
                    value={bulkPrice}
                    onChange={(e) => setBulkPrice(e.target.value)}
                    className="bg-black/40 border-white/20 w-24"
                    placeholder="€"
                  />
                  <Button onClick={handleApplyBulkPrice} disabled={saving} size="sm" className="btn-primary">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aplicar Precio'}
                  </Button>
                  <Button onClick={() => setSelectedDates([])} size="sm" variant="ghost">
                    <X className="w-4 h-4" />
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <h4 className="text-white font-medium text-lg">
                {format(currentMonth, 'MMMM yyyy', { locale })}
              </h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>

            {/* Weekday Headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {weekdays.map((day) => (
                <div key={day} className="text-center text-zinc-500 text-xs font-medium py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {calendarGrid.map((date, idx) => {
                if (!date) {
                  return <div key={`empty-${idx}`} className="aspect-square" />;
                }
                
                const dateStr = format(date, 'yyyy-MM-dd');
                const priceData = getPriceForDate(dateStr);
                const isSelected = selectedDates.includes(dateStr);
                const isPast = isBefore(date, new Date()) && !isToday(date);
                
                return (
                  <motion.div
                    key={dateStr}
                    onClick={() => !isPast && handleDateClick(dateStr)}
                    className={`
                      aspect-square p-1 rounded-lg border transition-all cursor-pointer
                      ${isPast ? 'opacity-40 cursor-not-allowed' : 'hover:border-primary/50'}
                      ${isSelected ? 'border-primary bg-primary/20' : 'border-white/10 bg-black/20'}
                      ${isToday(date) ? 'ring-2 ring-primary/50' : ''}
                      ${priceData?.is_custom ? 'bg-green-500/10 border-green-500/30' : ''}
                    `}
                    whileHover={!isPast ? { scale: 1.02 } : {}}
                    whileTap={!isPast ? { scale: 0.98 } : {}}
                    data-testid={`calendar-day-${dateStr}`}
                  >
                    <div className="h-full flex flex-col justify-between">
                      <span className={`text-xs ${isToday(date) ? 'text-primary font-bold' : 'text-zinc-400'}`}>
                        {format(date, 'd')}
                      </span>
                      {priceData && (
                        <div className="text-center">
                          <span className={`text-xs font-bold ${priceData.is_custom ? 'text-green-400' : 'text-zinc-300'}`}>
                            {priceData.price_euros.toFixed(0)}€
                          </span>
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Legend */}
            <div className="mt-4 flex items-center gap-6 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-green-500/30 border border-green-500/50"></div>
                <span className="text-zinc-400">Precio personalizado</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-black/20 border border-white/10"></div>
                <span className="text-zinc-400">Precio por defecto</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-primary/20 border border-primary"></div>
                <span className="text-zinc-400">Seleccionado</span>
              </div>
            </div>
          </div>

          {/* Custom Dates List */}
          {pricing?.custom_date_prices && Object.keys(pricing.custom_date_prices).length > 0 && (
            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-500" />
                Fechas con Precios Personalizados ({Object.keys(pricing.custom_date_prices).length})
              </h3>
              
              <div className="max-h-64 overflow-y-auto space-y-2">
                {Object.entries(pricing.custom_date_prices)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([date, cents]) => (
                    <div 
                      key={date}
                      className="flex items-center justify-between p-3 bg-black/20 rounded-lg hover:bg-black/30 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <span className="text-white font-medium">{date}</span>
                        <span className="text-primary font-bold">{(cents / 100).toFixed(2)}€</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveDate(date)}
                        className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default AdvancedPricing;
